export const REFERRAL_UID_KEY = 'referrer_uid';
export const REFERRAL_CAPTURED_AT_KEY = 'stocktag_referral_captured_at';
export const REFERRAL_BOUND_USER_KEY = 'stocktag_referral_bound_user_uid';
export const REFERRAL_INTRO_PENDING_KEY = 'stocktag_referral_intro_pending';
export const REFERRAL_INTRO_DISMISSED_AT_KEY = 'stocktag_referral_intro_dismissed_at';
export const REFERRAL_ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const FIREBASE_UID_MAX_LENGTH = 128;

export const normalizeReferralUid = (value = '') => {
  const uid = String(value || '').trim();
  if (!uid || uid.length > FIREBASE_UID_MAX_LENGTH) return '';
  if (/[\/\u0000-\u001f\u007f]/.test(uid)) return '';
  if (uid === '.' || uid === '..' || /^__.*__$/.test(uid)) return '';
  return uid;
};

const resolveStorage = (storage) => {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  return window.localStorage;
};

export const clearReferralAttribution = (storage) => {
  const target = resolveStorage(storage);
  if (!target) return;

  try {
    target.removeItem(REFERRAL_UID_KEY);
    target.removeItem(REFERRAL_CAPTURED_AT_KEY);
    target.removeItem(REFERRAL_BOUND_USER_KEY);
    target.removeItem(REFERRAL_INTRO_PENDING_KEY);
    target.removeItem(REFERRAL_INTRO_DISMISSED_AT_KEY);
  } catch {
    // Storage may be unavailable in privacy-restricted browsers.
  }
};

export const isReferralAttributionBoundToUser = ({ userUid, storage } = {}) => {
  const target = resolveStorage(storage);
  const normalizedUserUid = normalizeReferralUid(userUid);
  if (!target || !normalizedUserUid) return false;

  try {
    const referralUid = normalizeReferralUid(target.getItem(REFERRAL_UID_KEY));
    const boundUserUid = normalizeReferralUid(target.getItem(REFERRAL_BOUND_USER_KEY));
    return Boolean(referralUid && boundUserUid === normalizedUserUid);
  } catch {
    return false;
  }
};

export const captureReferralAttribution = ({
  url,
  storage,
  nowMs = Date.now()
} = {}) => {
  const target = resolveStorage(storage);
  const rawReferralUid = url?.searchParams?.get?.('ref');

  if (rawReferralUid === null || rawReferralUid === undefined) {
    return { captured: false, referralUid: '', reason: 'not_present', urlChanged: false };
  }

  url.searchParams.delete('ref');
  const referralUid = normalizeReferralUid(rawReferralUid);
  const capturedAtMs = Math.max(0, Math.floor(Number(nowMs) || 0));

  if (!target || !referralUid || !capturedAtMs) {
    clearReferralAttribution(target);
    return { captured: false, referralUid: '', reason: 'invalid_referrer', urlChanged: true };
  }

  try {
    target.setItem(REFERRAL_UID_KEY, referralUid);
    target.setItem(REFERRAL_CAPTURED_AT_KEY, String(capturedAtMs));
    target.removeItem(REFERRAL_BOUND_USER_KEY);
    target.setItem(REFERRAL_INTRO_PENDING_KEY, '1');
    target.removeItem(REFERRAL_INTRO_DISMISSED_AT_KEY);
  } catch {
    clearReferralAttribution(target);
    return { captured: false, referralUid: '', reason: 'storage_unavailable', urlChanged: true };
  }

  return { captured: true, referralUid, reason: 'captured', urlChanged: true };
};

export const captureReferralAttributionFromCurrentUrl = ({
  windowObject = typeof window !== 'undefined' ? window : null,
  storage,
  nowMs = Date.now()
} = {}) => {
  if (!windowObject?.location?.href) {
    return { captured: false, referralUid: '', reason: 'window_unavailable', urlChanged: false };
  }

  try {
    const url = new URL(windowObject.location.href);
    const result = captureReferralAttribution({ url, storage, nowMs });
    if (result.urlChanged) {
      windowObject.history?.replaceState?.({}, windowObject.document?.title || '', url.toString());
    }
    return result;
  } catch {
    return { captured: false, referralUid: '', reason: 'invalid_url', urlChanged: false };
  }
};

export const readReferralAttributionForUser = ({
  userUid,
  storage,
  nowMs = Date.now(),
  ttlMs = REFERRAL_ATTRIBUTION_TTL_MS
} = {}) => {
  const target = resolveStorage(storage);
  const normalizedUserUid = normalizeReferralUid(userUid);
  if (!target || !normalizedUserUid) {
    return { referralUid: '', reason: 'invalid_user' };
  }

  try {
    const referralUid = normalizeReferralUid(target.getItem(REFERRAL_UID_KEY));
    const capturedAtMs = Number(target.getItem(REFERRAL_CAPTURED_AT_KEY));
    const boundUserUid = normalizeReferralUid(target.getItem(REFERRAL_BOUND_USER_KEY));
    const ageMs = Number(nowMs) - capturedAtMs;
    const safeTtlMs = Math.max(0, Number(ttlMs) || 0);

    if (!referralUid || !Number.isFinite(capturedAtMs) || capturedAtMs <= 0) {
      clearReferralAttribution(target);
      return { referralUid: '', reason: 'missing_or_legacy_attribution' };
    }

    if (!Number.isFinite(ageMs) || ageMs < 0 || ageMs > safeTtlMs) {
      clearReferralAttribution(target);
      return { referralUid: '', reason: 'expired_attribution' };
    }

    if (referralUid === normalizedUserUid) {
      clearReferralAttribution(target);
      return { referralUid: '', reason: 'self_referral' };
    }

    if (boundUserUid && boundUserUid !== normalizedUserUid) {
      clearReferralAttribution(target);
      return { referralUid: '', reason: 'bound_to_another_user' };
    }

    target.setItem(REFERRAL_BOUND_USER_KEY, normalizedUserUid);
    return { referralUid, reason: boundUserUid ? 'already_bound' : 'bound' };
  } catch {
    clearReferralAttribution(target);
    return { referralUid: '', reason: 'storage_unavailable' };
  }
};
